import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Timer, ClockAlert, MailWarning, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Cron Health Dashboard — R107.
 *
 * Reads /api/admin/system/crons which parses .emergent/crons.yml, joins in
 * last-run signals from the audit log, and surfaces the email_queue
 * backlog so an admin can spot a broken scheduler or downstream email
 * failure without digging through logs.
 */
export default function CronHealthPage() {
  const [state, setState] = useState({ items: [], count: 0, email_queue_backlog: 0,
                                        webhook_cron_secret_configured: false });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/system/crons");
      setState(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load cron dashboard");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s so a just-fired cron shows up without manual reload.
  useEffect(() => {
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const staleSince = (iso) => {
    if (!iso) return null;
    const diffMs = Date.now() - new Date(iso).getTime();
    const hrs = Math.round(diffMs / (1000 * 60 * 60));
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  };

  return (
    <div className="p-8 space-y-6" data-testid="cron-health-page">
      <PageHeader
        title="Scheduled tasks"
        subtitle="Every automation running against this environment — schedule, last-observed run, and any backlog signals."
        actions={
          <Button variant="outline" onClick={load} className="border-white/10 gap-1 h-9" data-testid="cron-health-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cron-health-kpis">
        <Card className="border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
            <Timer className="h-3.5 w-3.5" /> Registered crons
          </div>
          <div className="text-3xl font-semibold mt-2 text-emerald-300" data-testid="cron-health-count">
            {state.count}
          </div>
        </Card>
        <Card className="border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
            <MailWarning className="h-3.5 w-3.5" /> Email queue backlog
          </div>
          <div className={"text-3xl font-semibold mt-2 " + ((state.email_queue_backlog || 0) === 0 ? "text-emerald-300" : "text-amber-300")}
                data-testid="cron-health-email-backlog">
            {state.email_queue_backlog}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">
            {state.email_queue_backlog > 0 ? "Delivery is failing — check EMERGENT_EMAIL_KEY / Resend health" : "Nothing stuck"}
          </div>
        </Card>
        <Card className="border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
            <ShieldCheck className="h-3.5 w-3.5" /> Cron auth
          </div>
          <div className={"text-lg font-semibold mt-2 " + (state.webhook_cron_secret_configured ? "text-emerald-300" : "text-rose-300")}
                data-testid="cron-health-auth-status">
            {state.webhook_cron_secret_configured ? "Bearer secret set" : "MISSING — every call will 401"}
          </div>
        </Card>
      </div>

      <Card className="border-white/10 bg-white/[0.02]">
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">Loading crons…</div>
        ) : state.items.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            No crons defined in <code className="text-[11px]">.emergent/crons.yml</code>.
          </div>
        ) : (
          <div className="divide-y divide-white/5" data-testid="cron-health-list">
            {state.items.map((c) => {
              const stale = c.last_run_at ? staleSince(c.last_run_at) : null;
              const nextStr = c.next_run_at ? new Date(c.next_run_at).toLocaleString() : "—";
              const isStale = c.last_run_at && ((Date.now() - new Date(c.last_run_at).getTime()) > (48 * 3600 * 1000));
              return (
                <div key={c.name} className="p-4 flex items-start gap-4" data-testid={`cron-row-${c.name}`}>
                  <ClockAlert className={"h-4 w-4 mt-1 shrink-0 " + (isStale ? "text-amber-400" : "text-emerald-400")} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-semibold">{c.name}</div>
                      <Badge variant="outline" className="border-emerald-500/30 text-emerald-200 text-[10px] font-mono">
                        {c.cron}
                      </Badge>
                      {c.last_24h_count > 0 && (
                        <Badge variant="outline" className="border-orange-500/40 text-orange-300 text-[10px]">
                          {c.last_24h_count} in 24h
                        </Badge>
                      )}
                      {isStale && (
                        <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">
                          stale · {stale}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">{c.description}</div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Last run</div>
                        <div className="text-zinc-300" data-testid={`cron-last-${c.name}`}>
                          {c.last_run_at ? new Date(c.last_run_at).toLocaleString() : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Next run (approx)</div>
                        <div className="text-zinc-300" data-testid={`cron-next-${c.name}`}>{nextStr}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Endpoint</div>
                        <code className="text-[10px] text-zinc-300 break-all">{c.endpoint}</code>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
